/** @jsxImportSource @emotion/react */
import React, { useMemo } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Descriptions,
  Row,
  Space,
  Statistic,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  CloudUploadOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../../components/common/MainLayout';
import {
  useGetDebugTemplatesQuery,
  type ScenarioTemplate as SeedTemplate,
} from '../../api/seasonsAdminApi';
import { useListScenariosQuery } from '../../api/savesV2Api';
import type { ScenarioTemplate as DbTemplate } from '../../types/saves';

const { Text, Paragraph } = Typography;

const KIND_COLORS: Record<string, string> = {
  tutorial: 'geekblue',
  mechanical: 'cyan',
  explanatory: 'purple',
  competitive: 'volcano',
  campaign: 'magenta',
};

const emptyCell = <Text type="secondary">—</Text>;

const formatDate = (value?: string | null): string => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString('ru-RU');
};

const kindTag = (kind?: string | null) => (kind
  ? <Tag color={KIND_COLORS[kind] || 'default'}>{kind}</Tag>
  : emptyCell);

const asNumber = (value: unknown): number | null => (
  typeof value === 'number' && Number.isFinite(value) ? value : null
);

const asText = (value: unknown): string | null => (
  typeof value === 'string' && value.trim() ? value.trim() : null
);

const countOf = (value: unknown): number | null => (Array.isArray(value) ? value.length : null);

/** Нормализованный срез одной стороны — семена и строка БД зовут поля по-разному. */
interface SideFacts {
  title: string | null;
  kind: string | null;
  version: number | null;
  durationDays: number | null;
  startDate: string | null;
  beds: number | null;
  plantings: number | null;
  goals: number | null;
  /// «Есть ли приз» — сам список наград не показываем, важен факт: до колонки
  /// `rewards` в scenario_templates приз объявлялся в сиде и терялся на границе
  /// БД, и заметить это было нечем.
  hasRewards: string | null;
}

interface FactRow {
  key: string;
  label: string;
  value: string | number | null;
}

interface FieldDiff {
  key: string;
  label: string;
  seed: string;
  db: string;
}

type ReconStatus = 'seed_only' | 'db_only' | 'diverged' | 'synced';

interface ReconRow {
  slug: string;
  title: string;
  kind: string | null;
  seed: SeedTemplate | null;
  db: DbTemplate | null;
  seedFacts: SideFacts | null;
  dbFacts: SideFacts | null;
  status: ReconStatus;
  diffs: FieldDiff[];
}

const readSeed = (seed: SeedTemplate): SideFacts => ({
  title: asText(seed.title),
  kind: asText(seed.kind),
  version: asNumber(seed.version),
  durationDays: asNumber(seed.durationDays),
  startDate: asText(seed.worldSpec?.startDate),
  beds: countOf(seed.worldSpec?.beds),
  plantings: countOf(seed.worldSpec?.plantings),
  goals: countOf(seed.goals),
  hasRewards: seed.rewards ? 'да' : 'нет',
});

const readDb = (db: DbTemplate): SideFacts => {
  const world = db.worldSpec;
  return {
    title: asText(db.title),
    kind: asText(db.kind),
    version: asNumber(db.version),
    durationDays: asNumber(db.durationDays),
    startDate: asText(world?.startDate) || asText(world?.start_date),
    beds: countOf(world?.beds),
    plantings: countOf(world?.plantings),
    goals: countOf(db.goals),
    hasRewards: db.rewards ? 'да' : 'нет',
  };
};

/**
 * Поля, по которым решаем «каталог отстал». Зеркалят `comparable()` из
 * scripts/seedScenarioCatalog.js — это СОДЕРЖИМОЕ шаблона.
 *
 * ВЕРСИИ ЗДЕСЬ НЕТ, и это не упущение. Номера версий у двух сторон живут в
 * разных пространствах: в сиде это авторская версия шаблона (всегда 1), а в
 * каталоге — монотонный счётчик публикаций слага (ScenarioTemplatePublisher
 * берёт getNextVersionForSlug). Сравнение их между собой объявляло расхождение
 * у КАЖДОГО опубликованного сценария и — что хуже — лечилось «переиздать»
 * ровно в обратную сторону: каждая публикация увеличивала номер в БД и
 * отодвигала его от сидовой единицы ещё дальше.
 */
const COMPARED_FIELDS: Array<{ key: keyof SideFacts; label: string }> = [
  { key: 'title', label: 'Название' },
  { key: 'kind', label: 'Вид' },
  { key: 'durationDays', label: 'Длительность, дней' },
  { key: 'startDate', label: 'Старт мира' },
  { key: 'beds', label: 'Грядок' },
  { key: 'plantings', label: 'Посадок' },
  { key: 'goals', label: 'Целей' },
  { key: 'hasRewards', label: 'Приз объявлен' },
];

/**
 * Отсутствие поля на одной из сторон — не расхождение: список каталога может не
 * отдавать worldSpec/durationDays вовсе. Сравниваем только то, что дали обе.
 */
const buildDiffs = (seed: SideFacts, db: SideFacts): FieldDiff[] => {
  const diffs: FieldDiff[] = [];
  COMPARED_FIELDS.forEach(({ key, label }) => {
    const left = seed[key];
    const right = db[key];
    if (left === null || right === null) return;
    if (String(left) === String(right)) return;
    diffs.push({ key: String(key), label, seed: String(left), db: String(right) });
  });
  return diffs;
};

const statusTag = (status: ReconStatus) => {
  if (status === 'seed_only') {
    return (
      <Tooltip title="Живёт только в backend-сидах: Unity и сезоны его играют, игровая галерея не видит.">
        <Tag color="orange">только в сидах</Tag>
      </Tooltip>
    );
  }
  if (status === 'db_only') {
    return (
      <Tooltip title="Строка в scenario_templates без канонического семени: галерея показывает, играть нечего.">
        <Tag color="purple">только в БД</Tag>
      </Tooltip>
    );
  }
  if (status === 'diverged') {
    return (
      <Tooltip title="Есть с обеих сторон, но содержимое разъехалось — каталог отдаёт не то, что играет ядро. Номера версий не сравниваются: в БД это счётчик публикаций.">
        <Tag color="red">расхождение</Tag>
      </Tooltip>
    );
  }
  return <Tag color="green">синхронизирован</Tag>;
};

const factRows = (facts: SideFacts, extra: FactRow[] = []): FactRow[] => ([
  { key: 'title', label: 'Название', value: facts.title },
  { key: 'kind', label: 'Вид', value: facts.kind },
  { key: 'version', label: 'Версия', value: facts.version },
  { key: 'durationDays', label: 'Длительность, дней', value: facts.durationDays },
  { key: 'startDate', label: 'Старт мира', value: facts.startDate },
  ...extra,
  { key: 'beds', label: 'Грядок', value: facts.beds },
  { key: 'plantings', label: 'Посадок', value: facts.plantings },
  { key: 'goals', label: 'Целей', value: facts.goals },
]);

const FactsPanel: React.FC<{
  title: string;
  rows: FactRow[] | null;
  diffKeys: Set<string>;
  emptyText: string;
}> = ({ title, rows, diffKeys, emptyText }) => (
  <Card size="small" title={title}>
    {rows ? (
      <Descriptions size="small" column={1} bordered>
        {rows.map((row) => {
          const highlighted = diffKeys.has(row.key);
          const value = row.value === null || row.value === ''
            ? emptyCell
            : <Text type={highlighted ? 'danger' : undefined} strong={highlighted}>{row.value}</Text>;
          return (
            <Descriptions.Item key={row.key} label={row.label}>{value}</Descriptions.Item>
          );
        })}
      </Descriptions>
    ) : (
      <Text type="secondary">{emptyText}</Text>
    )}
  </Card>
);

const ScenarioCatalogPage: React.FC = () => {
  const navigate = useNavigate();
  const {
    data: seedsData,
    isFetching: seedsLoading,
    error: seedsError,
    refetch: refetchSeeds,
  } = useGetDebugTemplatesQuery();
  const {
    data: catalogData,
    isFetching: catalogLoading,
    error: catalogError,
    refetch: refetchCatalog,
  } = useListScenariosQuery({ limit: 200 });

  const seeds = useMemo<SeedTemplate[]>(() => seedsData?.templates || [], [seedsData]);
  const catalog = useMemo<DbTemplate[]>(() => catalogData || [], [catalogData]);

  const rows = useMemo<ReconRow[]>(() => {
    const dbBySlug = new Map<string, DbTemplate>();
    catalog.forEach((item) => {
      const previous = dbBySlug.get(item.slug);
      // Каталог может отдать несколько версий одного slug — держим старшую.
      if (!previous || (asNumber(item.version) ?? 0) > (asNumber(previous.version) ?? 0)) {
        dbBySlug.set(item.slug, item);
      }
    });

    const result: ReconRow[] = seeds.map((seed) => {
      const db = dbBySlug.get(seed.slug) || null;
      const seedFacts = readSeed(seed);
      const dbFacts = db ? readDb(db) : null;
      const diffs = dbFacts ? buildDiffs(seedFacts, dbFacts) : [];
      const status: ReconStatus = !db
        ? 'seed_only'
        : diffs.length > 0 ? 'diverged' : 'synced';
      return {
        slug: seed.slug,
        title: seedFacts.title || seed.slug,
        kind: seedFacts.kind,
        seed,
        db,
        seedFacts,
        dbFacts,
        status,
        diffs,
      };
    });

    const seedSlugs = new Set(seeds.map((seed) => seed.slug));
    dbBySlug.forEach((db) => {
      if (seedSlugs.has(db.slug)) return;
      const dbFacts = readDb(db);
      result.push({
        slug: db.slug,
        title: dbFacts.title || db.slug,
        kind: dbFacts.kind,
        seed: null,
        db,
        seedFacts: null,
        dbFacts,
        status: 'db_only',
        diffs: [],
      });
    });

    return result;
  }, [seeds, catalog]);

  const outOfSync = useMemo(
    () => rows.filter((row) => row.status !== 'synced').length,
    [rows],
  );

  const errorDetail = (err: unknown): string | null => {
    if (!err) return null;
    const detail = (err as { data?: { message?: string } })?.data?.message;
    return detail || 'запрос не удался';
  };

  const columns: ColumnsType<ReconRow> = [
    {
      title: 'Сценарий',
      key: 'slug',
      render: (_, row) => (
        <Space direction="vertical" size={0}>
          <Text strong>{row.title}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>{row.slug}</Text>
        </Space>
      ),
    },
    {
      title: 'Вид',
      key: 'kind',
      width: 130,
      render: (_, row) => kindTag(row.kind),
    },
    {
      title: 'В сидах',
      key: 'seed',
      width: 190,
      render: (_, row) => {
        if (!row.seedFacts) return <Tag>нет</Tag>;
        const duration = row.seedFacts.durationDays;
        return (
          <Space size={4}>
            <Tag icon={<CheckCircleOutlined />} color="green">
              v{row.seedFacts.version ?? '—'}
            </Tag>
            <Text type="secondary">{duration === null ? '—' : `${duration}д`}</Text>
          </Space>
        );
      },
    },
    {
      title: 'В каталоге (БД)',
      key: 'db',
      width: 250,
      render: (_, row) => {
        if (!row.db || !row.dbFacts) {
          return <Tag icon={<CloseCircleOutlined />} color="red">не опубликован</Tag>;
        }
        return (
          <Space direction="vertical" size={0}>
            <Tag icon={<CheckCircleOutlined />} color="green" style={{ marginInlineEnd: 0 }}>
              v{row.dbFacts.version ?? '—'}
            </Tag>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {formatDate(row.db.publishedAt)}
            </Text>
          </Space>
        );
      },
    },
    {
      title: 'Статус',
      key: 'status',
      width: 170,
      render: (_, row) => statusTag(row.status),
    },
    {
      title: 'Действия',
      key: 'actions',
      width: 170,
      render: (_, row) => {
        if (!row.seed) {
          return (
            <Tooltip title="Публиковать нечего: канонического семени с таким slug нет, spec собирать не из чего.">
              <Button size="small" disabled icon={<CloudUploadOutlined />}>Опубликовать</Button>
            </Tooltip>
          );
        }
        return (
          <Button
            size="small"
            type={row.status === 'synced' ? 'default' : 'primary'}
            icon={<CloudUploadOutlined />}
            onClick={() => navigate(`/admin/scenarios/author?seed=${encodeURIComponent(row.slug)}`)}
          >
            {row.status === 'synced' ? 'Переиздать' : 'Опубликовать'}
          </Button>
        );
      },
    },
  ];

  return (
    <MainLayout>
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Card
            title="Каталог сценариев"
            loading={seedsLoading && catalogLoading && rows.length === 0}
            extra={(
              <Button
                icon={<ReloadOutlined />}
                onClick={() => { refetchSeeds(); refetchCatalog(); }}
              >
                Обновить
              </Button>
            )}
          >
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              message="Источник правды для игры — канонические семена в backend/src/data/seeds/scenarioTemplates.js: именно их играет Unity и по ним считаются сезоны. Игровая галерея читает другое — таблицу scenario_templates, а пишет в неё только POST /api/scenario-generator/publish, поэтому всё, что не опубликовано, для игрока просто не существует."
            />
            <Paragraph type="secondary" style={{ marginBottom: 16 }}>
              Генератор v1 публикует только полив и подкормку (PUBLISHABLE_ACTIONS) — сценарий,
              построенный вокруг сбора, через publish целиком не проедет: авторинг положит в
              каталог урезанную ветку. Чтобы залить семена в каталог КАК ЕСТЬ, есть команда
              <Text code>npm run scenarios:sync</Text> (<Text code>--dry-run</Text> покажет, что
              изменится) — она и есть канонический путь синхронизации, а авторинг остаётся для
              ручной правки spec. И помните про видимость: строка со статусом «черновик» пишется
              в БД, но каталог отдаёт только опубликованные — расхождение такой публикацией не
              лечится. Отдельно: ограничение цели по числу действий (actionCount) проверяется
              на лету, но не на завершении — в отладочной консоли такая цель может показать
              FAIL, а в сохранённом счёте PASS.
            </Paragraph>

            {seedsError && (
              <Alert
                type="error"
                showIcon
                style={{ marginBottom: 16 }}
                message={`Семена не загрузились: ${errorDetail(seedsError)}`}
              />
            )}
            {catalogError && (
              <Alert
                type="error"
                showIcon
                style={{ marginBottom: 16 }}
                message={`Каталог не загрузился: ${errorDetail(catalogError)}`}
              />
            )}

            <Row gutter={16}>
              <Col span={8}>
                <Statistic title="Сидов (канон)" value={seeds.length} />
              </Col>
              <Col span={8}>
                <Statistic title="В каталоге (БД)" value={catalog.length} />
              </Col>
              <Col span={8}>
                <Statistic
                  title="Расходится"
                  value={outOfSync}
                  valueStyle={outOfSync > 0 ? { color: '#cf1322' } : undefined}
                />
              </Col>
            </Row>
          </Card>
        </Col>

        <Col span={24}>
          <Card size="small" title="Сверка семян и опубликованного каталога">
            <Table<ReconRow>
              rowKey={(row) => row.slug}
              columns={columns}
              dataSource={rows}
              loading={seedsLoading || catalogLoading}
              pagination={{ pageSize: 20, hideOnSinglePage: true }}
              locale={{ emptyText: 'Ни семян, ни строк каталога' }}
              expandable={{
                expandedRowRender: (row) => {
                  const diffKeys = new Set(row.diffs.map((diff) => diff.key));
                  return (
                    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                      <Row gutter={16}>
                        <Col span={12}>
                          <FactsPanel
                            title="Семя (backend seeds)"
                            rows={row.seedFacts
                              ? factRows(row.seedFacts, [
                                { key: 'terminalDate', label: 'Терминальная дата', value: asText(row.seed?.terminalDate) },
                              ])
                              : null}
                            diffKeys={diffKeys}
                            emptyText="Канонического семени с таким slug нет."
                          />
                        </Col>
                        <Col span={12}>
                          <FactsPanel
                            title="Каталог (scenario_templates)"
                            rows={row.dbFacts
                              ? factRows(row.dbFacts, [
                                { key: 'publishedAt', label: 'Опубликован', value: formatDate(row.db?.publishedAt) },
                                { key: 'visibility', label: 'Видимость', value: asText(row.db?.visibility) },
                              ])
                              : null}
                            diffKeys={diffKeys}
                            emptyText="Строки в каталоге нет — сценарий не опубликован, галерея его не покажет."
                          />
                        </Col>
                      </Row>

                      {row.seedFacts && row.dbFacts && (
                        row.diffs.length > 0 ? (
                          <Card size="small" title={`Расхождения (${row.diffs.length})`}>
                            <Table<FieldDiff>
                              rowKey={(diff) => diff.key}
                              size="small"
                              pagination={false}
                              dataSource={row.diffs}
                              columns={[
                                { title: 'Поле', dataIndex: 'label', width: 220 },
                                {
                                  title: 'Семя',
                                  dataIndex: 'seed',
                                  render: (value: string) => <Text type="danger">{value}</Text>,
                                },
                                {
                                  title: 'Каталог (БД)',
                                  dataIndex: 'db',
                                  render: (value: string) => <Text type="danger">{value}</Text>,
                                },
                              ]}
                            />
                          </Card>
                        ) : (
                          <Text type="secondary">
                            Сравнимые поля совпадают. Поля, которых нет на одной из сторон,
                            показаны как «—» и расхождением не считаются.
                          </Text>
                        )
                      )}
                    </Space>
                  );
                },
              }}
            />
          </Card>
        </Col>
      </Row>
    </MainLayout>
  );
};

export default ScenarioCatalogPage;
